import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  model,
  Output,
  signal,
  viewChild,
} from "@angular/core";
import * as d3 from "d3";

import { GatherMeta } from "../../../../models/segy";

/** Space reserved around the plot area for the D3 axes and the meta caption. */
const MARGIN = { top: 20, right: 14, bottom: 40, left: 50 } as const;

/** Minimum drag distance (px) before a press is treated as a zoom box rather
 * than a trace-selecting click. */
const DRAG_THRESHOLD = 4;

/**
 * A zoom window expressed as fractions [0,1] of the full trace (x) and time
 * (y) domains. Being resolution-independent it can be shared verbatim between
 * the two compare panels (whose images may differ in pixel size). null = the
 * full, unzoomed view.
 */
export interface GatherZoom {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/**
 * Presentational shot-gather viewer: stretches a server-rendered base64 PNG
 * into the plot area and draws D3 axes around it from the gather meta
 * (x: trace index, y: time in seconds), following the spirit of
 * shot-image/d3-image. Clicking the image inverts the x scale to a trace
 * index and emits `traceClick`; the selected trace is highlighted with a
 * vertical line.
 *
 * Drag a rubber-band box over the image to zoom into a trace/time window —
 * the image is enlarged and clipped to that window and the axes redraw to the
 * zoomed domain, so the same region can be compared before/after filtering.
 * Zoom is a two-way `model` so callers (the compare view) can sync the window
 * across panels; left at its default it is managed internally.
 */
@Component({
  selector: "app-segy-gather-viewer",
  templateUrl: "./gather-viewer.component.html",
  styleUrl: "./gather-viewer.component.less",
  standalone: false,
})
export class GatherViewerComponent {
  // ─────────────────────────────────────────────────────────────
  // INPUTS / OUTPUTS
  // ─────────────────────────────────────────────────────────────

  /** Base64-encoded PNG of the gather (without the data-URL prefix). */
  public readonly image = input<string | null>(null);
  /** Render meta returned alongside the image; drives the axes. */
  public readonly meta = input<GatherMeta | null>(null);
  public readonly loading = input<boolean>(false);
  /** Trace highlighted with a vertical marker line (null = none). */
  public readonly selectedTraceIndex = input<number | null>(null);

  /** Current zoom window (two-way). Left at the default `null` for the single
   * viewer; the compare view binds both panels to one signal so a zoom in
   * either pane frames the same trace/time window in both. */
  public readonly zoom = model<GatherZoom | null>(null);

  /** Emits the (original, undecimated) trace index that was clicked. */
  @Output() traceClick = new EventEmitter<number>();

  // ─────────────────────────────────────────────────────────────
  // VIEW STATE
  // ─────────────────────────────────────────────────────────────

  private container = viewChild<ElementRef<HTMLDivElement>>("container");
  private axesSvg = viewChild<ElementRef<SVGSVGElement>>("axes");

  /** Measured container size, kept current by a ResizeObserver. */
  private containerSize = signal<{ width: number; height: number } | null>(
    null,
  );

  /** In-progress rubber-band drag, in plot-area pixel coords (null = none). */
  private readonly dragStart = signal<{ x: number; y: number } | null>(null);
  private readonly dragCurrent = signal<{ x: number; y: number } | null>(null);

  /** Pixel rectangle of the plot area (the stretched image) in the container. */
  protected readonly plotRect = computed<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(() => {
    const size = this.containerSize();
    if (!size) return null;
    const width = size.width - MARGIN.left - MARGIN.right;
    const height = size.height - MARGIN.top - MARGIN.bottom;
    if (width < 20 || height < 20) return null;
    return { left: MARGIN.left, top: MARGIN.top, width, height };
  });

  protected readonly imageSrc = computed<string | null>(() => {
    const image = this.image();
    return image ? `data:image/png;base64,${image}` : null;
  });

  protected readonly isZoomed = computed<boolean>(() => this.zoom() != null);

  /** Pixel box for the (possibly zoomed) image inside the clipped plot area.
   * Unzoomed it fills the plot rect; zoomed it is enlarged and shifted so the
   * selected fraction fills the rect (the remainder is clipped away by the
   * plot area's overflow:hidden). */
  protected readonly imageBox = computed<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(() => {
    const rect = this.plotRect();
    if (!rect) return null;
    const z = this.zoom();
    if (!z) return { left: 0, top: 0, width: rect.width, height: rect.height };
    const width = rect.width / (z.x1 - z.x0);
    const height = rect.height / (z.y1 - z.y0);
    return { left: -z.x0 * width, top: -z.y0 * height, width, height };
  });

  /** Rubber-band rectangle to draw while dragging (plot-area px), or null. */
  protected readonly selectionRect = computed<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(() => {
    const start = this.dragStart();
    const current = this.dragCurrent();
    if (!start || !current) return null;
    return {
      left: Math.min(start.x, current.x),
      top: Math.min(start.y, current.y),
      width: Math.abs(current.x - start.x),
      height: Math.abs(current.y - start.y),
    };
  });

  private resizeObserver?: ResizeObserver;

  constructor() {
    afterNextRender(() => {
      const el = this.container()?.nativeElement;
      if (!el) return;
      this.resizeObserver = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (rect && rect.width > 0 && rect.height > 0) {
          this.containerSize.set({ width: rect.width, height: rect.height });
        }
      });
      this.resizeObserver.observe(el);
      this.containerSize.set({
        width: el.clientWidth,
        height: el.clientHeight,
      });
    });
    inject(DestroyRef).onDestroy(() => this.resizeObserver?.disconnect());

    // redraw the axes whenever the meta, container size or selection change
    effect(() => this.drawAxes());
  }

  // ─────────────────────────────────────────────────────────────
  // INTERACTION
  // ─────────────────────────────────────────────────────────────

  /** Trace/time domains currently in view, accounting for the zoom window. */
  private viewDomain(): {
    traceLo: number;
    traceHi: number;
    timeLo: number;
    timeHi: number;
  } | null {
    const meta = this.meta();
    if (!meta) return null;
    const z = this.zoom();
    const x0 = z?.x0 ?? 0;
    const x1 = z?.x1 ?? 1;
    const y0 = z?.y0 ?? 0;
    const y1 = z?.y1 ?? 1;
    return {
      traceLo: x0 * meta.n_traces,
      traceHi: x1 * meta.n_traces,
      timeLo: y0 * meta.t_max,
      timeHi: y1 * meta.t_max,
    };
  }

  /** Cursor position relative to the plot area (robust to the enlarged,
   * offset image that sits on top of it while zoomed). */
  private localPoint(event: MouseEvent): { x: number; y: number } {
    const bounds = (
      event.currentTarget as HTMLElement
    ).getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  }

  protected onPointerDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    const point = this.localPoint(event);
    this.dragStart.set(point);
    this.dragCurrent.set(point);
  }

  protected onPointerMove(event: MouseEvent): void {
    if (!this.dragStart()) return;
    this.dragCurrent.set(this.localPoint(event));
  }

  protected onPointerUp(event: MouseEvent): void {
    const start = this.dragStart();
    if (!start) return;
    const end = this.localPoint(event);
    this.dragStart.set(null);
    this.dragCurrent.set(null);

    // a press that barely moved is a trace-selecting click; a real drag zooms
    if (
      Math.abs(end.x - start.x) < DRAG_THRESHOLD &&
      Math.abs(end.y - start.y) < DRAG_THRESHOLD
    ) {
      this.selectTraceAt(start.x);
    } else {
      this.zoomToBox(start, end);
    }
  }

  /** Abandon an in-progress drag if the cursor leaves the plot area. */
  protected onPointerLeave(): void {
    this.dragStart.set(null);
    this.dragCurrent.set(null);
  }

  /** Map a plot-area x to a trace index via the (zoomed) x-scale invert. */
  private selectTraceAt(px: number): void {
    const meta = this.meta();
    const rect = this.plotRect();
    const dom = this.viewDomain();
    if (!meta || !rect || !dom || !(meta.n_traces > 0)) return;
    const xScale = d3
      .scaleLinear()
      .domain([dom.traceLo, dom.traceHi])
      .range([0, rect.width]);
    const index = Math.floor(xScale.invert(px));
    this.traceClick.emit(Math.max(0, Math.min(meta.n_traces - 1, index)));
  }

  /** Compose a pixel selection within the current view into a new fractional
   * zoom window (so successive zooms nest correctly). */
  private zoomToBox(
    a: { x: number; y: number },
    b: { x: number; y: number },
  ): void {
    const rect = this.plotRect();
    if (!rect) return;
    const left = Math.max(0, Math.min(a.x, b.x));
    const right = Math.min(rect.width, Math.max(a.x, b.x));
    const top = Math.max(0, Math.min(a.y, b.y));
    const bottom = Math.min(rect.height, Math.max(a.y, b.y));
    if (right - left < DRAG_THRESHOLD || bottom - top < DRAG_THRESHOLD) return;

    const z = this.zoom() ?? { x0: 0, x1: 1, y0: 0, y1: 1 };
    const spanX = z.x1 - z.x0;
    const spanY = z.y1 - z.y0;
    this.zoom.set({
      x0: z.x0 + (left / rect.width) * spanX,
      x1: z.x0 + (right / rect.width) * spanX,
      y0: z.y0 + (top / rect.height) * spanY,
      y1: z.y0 + (bottom / rect.height) * spanY,
    });
  }

  protected resetZoom(): void {
    this.zoom.set(null);
  }

  // ─────────────────────────────────────────────────────────────
  // D3 AXES
  // ─────────────────────────────────────────────────────────────

  private drawAxes(): void {
    const svgEl = this.axesSvg()?.nativeElement;
    if (!svgEl) return;

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();

    const meta = this.meta();
    const rect = this.plotRect();
    const dom = this.viewDomain();
    if (!meta || !rect || !dom) return;

    const xScale = d3
      .scaleLinear()
      .domain([dom.traceLo, dom.traceHi])
      .range([0, rect.width]);
    const yScale = d3
      .scaleLinear()
      .domain([dom.timeLo, dom.timeHi])
      .range([0, rect.height]);

    const g = svg
      .append("g")
      .attr("transform", `translate(${rect.left}, ${rect.top})`);

    g.append("g")
      .attr("transform", `translate(0, ${rect.height})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(Math.min(10, Math.max(2, meta.n_traces)))
          .tickSizeOuter(0),
      );
    g.append("g").call(d3.axisLeft(yScale).ticks(8).tickSizeOuter(0));

    // axis labels (styled inline: d3-created nodes don't get the emulated
    // view-encapsulation attribute, so component CSS can't reach them)
    g.append("text")
      .attr("x", rect.width / 2)
      .attr("y", rect.height + 32)
      .attr("text-anchor", "middle")
      .attr("fill", "#666")
      .style("font-size", "11px")
      .text("Trace");
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -rect.height / 2)
      .attr("y", -36)
      .attr("text-anchor", "middle")
      .attr("fill", "#666")
      .style("font-size", "11px")
      .text("Time (s)");

    // vertical highlight at the selected trace (centred on its column),
    // drawn only when it falls inside the current (zoomed) view
    const selected = this.selectedTraceIndex();
    if (
      selected != null &&
      selected + 0.5 >= dom.traceLo &&
      selected + 0.5 <= dom.traceHi
    ) {
      g.append("line")
        .attr("x1", xScale(selected + 0.5))
        .attr("x2", xScale(selected + 0.5))
        .attr("y1", 0)
        .attr("y2", rect.height)
        .attr("stroke", "#ff4d4f")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "4,3")
        .attr("pointer-events", "none");
    }
  }

  // ─────────────────────────────────────────────────────────────
  // FORMATTING
  // ─────────────────────────────────────────────────────────────

  /** Compact amplitude formatting for the caption (seismic values span
   * many orders of magnitude, so fall back to exponential notation). */
  protected formatAmp(value: number | null | undefined): string {
    if (value == null || !isFinite(value)) return "-";
    if (value === 0) return "0";
    const abs = Math.abs(value);
    if (abs >= 1000 || abs < 0.01) return value.toExponential(2);
    return Number(value.toPrecision(3)).toString();
  }
}
